import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, ClipboardPaste, Copy, FileInput, FolderPlus, Layers, ListPlus, Trash2 } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { PapierkorbFenster, RueckgaengigHinweis } from '@/components/Papierkorb'
import { ZeilenListe, type Spalte } from '@/components/zeilenblock'
import { ZiehBereich, useRandMarkieren, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { verschiebeMehrereVor } from '@/components/ziehen/ziehen'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige, frage } from '@/lib/dialoge'
import { inlineInput } from '@/lib/form-styles'
import {
  copyPositions,
  createGroup,
  createPosition,
  deleteGroup,
  deletePositions,
  fetchClipboardCount,
  fetchPositionTrash,
  fetchRaster,
  fetchUnits,
  pastePositions,
  reorderPositions,
  restorePositions,
} from '../api/calculation-positions.api'
import { naechsteStufeAuf, naechsteStufeZu } from '../lib/gruppen-stufen'
import { liesKlappZustand, merkeKlappZustand } from '../lib/klapp-zustand'
import { gewichtText, stundenText } from '../lib/kalkulation-labels'
import { POSITIONS_ART_FILTER, passtArt, type PositionsArtFilter } from '../lib/positions-filter'
import type { CalcGroup, CalcPosition, CalcRaster, CalcRasterSum, CalcUnit, Calculation } from '../types/calculation.types'
import { BlattAnwendenDialog } from './BlattAnwendenDialog'
import { PositionsZeile, ZUSTAND_ANZEIGE } from './PositionsZeile'

const stundenGenau = (h: number) => `${h.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 3 })} h`

/**
 * Nachtrag 7 (N7-6) — **die Summen unter der Positionsübersicht**: Material, Lohn, Stunden, Aufschläge und
 * Gewicht, eine Zeile je Einheit und eine für die ganze Menge. „Je Einheit“ ist die Summe der Werte je Einheit
 * aller Positionen (die Summe der Spalten Std/E, Lohn/E, Mat/E) — die Positionen haben verschiedene Einheiten,
 * einen Preis je Einheit der ganzen Kalkulation gibt es nicht. Ohne `calculation.cost.view` bleiben Stunden
 * und Gewicht; die Kosten schickt der Server dann gar nicht mit.
 */
function Summenzeile({ summen, kosten }: { summen: NonNullable<CalcRaster['sums']>; kosten: boolean }) {
  const spalten: { titel: string; hinweis?: string; wert: (s: CalcRasterSum, je: boolean) => string }[] = [
    ...(kosten
      ? [
          { titel: 'Material', hinweis: 'Einzelkosten ohne Lohn: Material, Fremdleistung, Gerät, Sonstiges', wert: (s: CalcRasterSum) => geldText(s.costMaterial ?? 0) },
          { titel: 'Lohn', wert: (s: CalcRasterSum) => geldText(s.costLabor ?? 0) },
        ]
      : []),
    { titel: 'Stunden', wert: (s, je) => (je ? stundenGenau(s.hours) : `${stundenText(s.hours)} h`) },
    ...(kosten ? [{ titel: 'Aufschläge', hinweis: 'Was die Aufschläge ausmachen: kalkulierter Preis ohne die Einzelkosten', wert: (s: CalcRasterSum) => geldText(s.surchargeTotal ?? 0) }] : []),
    { titel: 'Gewicht', wert: (s) => gewichtText(s.weight) || '—' },
  ]
  const zeilen: { titel: string; hinweis: string; satz: CalcRasterSum; je: boolean }[] = [
    { titel: 'je Einheit', hinweis: 'Summe der Werte je Einheit aller Positionen, die in die Angebotssumme zählen', satz: summen.perUnit, je: true },
    { titel: 'gesamt', hinweis: 'für die ganze Menge aller Positionen, die in die Angebotssumme zählen', satz: summen.total, je: false },
  ]
  return (
    <div
      role="table"
      aria-label="Summen der Positionen"
      className="mt-1 grid justify-end gap-x-5 gap-y-0.5 overflow-x-auto border-t border-border pt-1 text-xs tabular-nums"
      style={{ gridTemplateColumns: `auto repeat(${spalten.length}, minmax(4.5rem, auto))` }}
      data-summen
    >
      <div role="row" className="contents text-muted-foreground">
        <span role="columnheader" />
        {spalten.map((s) => (
          <span key={s.titel} role="columnheader" className="text-right" title={s.hinweis}>
            {s.titel}
          </span>
        ))}
      </div>
      {zeilen.map((z) => (
        <div key={z.titel} role="row" className="contents">
          <span role="rowheader" className="text-muted-foreground" title={z.hinweis}>
            {z.titel}
          </span>
          {spalten.map((s) => (
            <span key={s.titel} role="cell" className={`text-right ${z.je ? '' : 'font-medium'}`}>
              {s.wert(z.satz, z.je)}
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Reihenfolge wie im Projektmanagement (Stückliste, Verkauf): **Menge, Einheit,
 * Bezeichnung** — so nennt der Betrieb eine Position („12 lfm Geländer").
 * Die Spalte Nr./OZ wächst mit der längsten OZ (`nrSpalte`).
 */
const SPALTEN_BASIS: Spalte[] = [
  { key: 'nr', label: 'Nr./OZ', spur: '4.5rem', min: 56 },
  { key: 'menge', label: 'Menge', spur: '5.5rem', min: 72, rechts: true },
  { key: 'einheit', label: 'Einheit', spur: '5rem', min: 64 },
  { key: 'kurztext', label: 'Bezeichnung', spur: 'minmax(14rem,3fr)', min: 220, schmal: 'w-full' },
  { key: 'std', label: 'Std/E', spur: '5.5rem', min: 70, rechts: true },
  // N1-28: Lohn/E in Euro neben Std/E — beide auf denselben Arbeitsschritt, je nach Eingabe umgerechnet.
  { key: 'lohn', label: 'Lohn/E', spur: '6.5rem', min: 80, rechts: true },
  { key: 'mat', label: 'Mat/E', spur: '6.5rem', min: 80, rechts: true },
  { key: 'ep', label: 'EP', spur: '7rem', min: 90, rechts: true },
  { key: 'gp', label: 'GP', spur: '7.5rem', min: 100, rechts: true },
  { key: 'zustand', label: 'Zustand', spur: '7.5rem', min: 96 },
  { key: 'aktion', label: '', spur: '3.5rem', min: 50 },
]

/**
 * AP-37 4.2, 4.8, 4.9 — **Reiter Positionen**: das Raster wie das Belegraster
 * (Nr./OZ · Kurztext · Menge · Einheit · Std/E · Mat/E · EP · GP · Zustand),
 * Gruppenzeilen mit Zwischensumme und einklappbar, Ergebnisleiste am Fuß.
 *
 * Werkzeuge wie Stückliste und Belegraster (`components/ziehen`): an der freien
 * Fläche greifen und ziehen, am linken Rand markieren, Markierte gemeinsam
 * ziehen, Strg+C kopiert in die Ablage, Strg+V fügt ein, Filter, Papierkorb.
 * Die leere letzte Zeile legt beim Verlassen an; Enter springt in dieselbe Spalte
 * der nächsten Zeile (AP-32 Nachtrag 11).
 */
export function PositionenTab({
  kalkulation,
  onGeaendert,
  onOeffnen,
  lvEinlesen,
}: {
  kalkulation: Calculation
  onGeaendert: () => void
  onOeffnen: (positionId: string) => void
  /** Einstieg „LV einlesen" in der leeren Kalkulation — nur mit `.LV` (4.9). */
  lvEinlesen?: () => void
}) {
  const { user } = useAuthStore()
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  const darf = kalkulation.writable && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  // N1-31: Mit LV keine neuen Positionen und Gruppen — das LV gibt beides vor, ausgepreist wird nur.
  const mitLv = Boolean(kalkulation.hasLv)
  const [raster, setRaster] = useState<CalcRaster | null>(null)
  const [einheiten, setEinheiten] = useState<CalcUnit[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  // Nachtrag 6 (N6-2): Was zugeklappt war, steht beim Zurückkommen wieder so da — je Kalkulation gemerkt.
  const [gemerkt] = useState(() => liesKlappZustand(kalkulation.id))
  const [zu, setZu] = useState<Set<string>>(() => new Set(gemerkt.zu))
  const [suche, setSuche] = useState('')
  const [zustand, setZustand] = useState('')
  const [gruppeFilter, setGruppeFilter] = useState('')
  const [artFilter, setArtFilter] = useState<PositionsArtFilter>('')
  // N1-22: Einleitungs- und Langtexte unter den Positionen, wie im gedruckten LV — gemerkt je Browser.
  const [texte, setTexte] = useState<boolean>(() => {
    try {
      return localStorage.getItem('symphenia.kalkulation.texteEinblenden') === '1'
    } catch {
      return false
    }
  })
  // N1-24: je Position auf- oder zuklappen — die Menge hält die Abweichungen vom Schalter „Texte einblenden“.
  const [umgeschaltet, setUmgeschaltet] = useState<Set<string>>(() => new Set(gemerkt.texte))
  const textOffen = (id: string) => texte !== umgeschaltet.has(id)
  const zeigeTexte = useCallback((an: boolean) => {
    setTexte(an)
    setUmgeschaltet(new Set())
    try {
      localStorage.setItem('symphenia.kalkulation.texteEinblenden', an ? '1' : '0')
    } catch {
      // ohne Speicher nur für diese Sitzung
    }
  }, [])
  const [neuSchluessel, setNeuSchluessel] = useState(0)
  const [neuFokus, setNeuFokus] = useState(false)
  const [beginnen, setBeginnen] = useState(false)
  const [korbOffen, setKorbOffen] = useState(false)
  const [geloescht, setGeloescht] = useState<{ trashId: string; name: string }[]>([])
  const [ablage, setAblage] = useState(0)
  const [anwendenOffen, setAnwendenOffen] = useState(false)
  const behaelter = useRef<HTMLDivElement>(null)

  const laden = useCallback(async () => {
    try {
      setRaster(await fetchRaster(kalkulation.id))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Positionen ließen sich nicht laden.'))
    }
  }, [kalkulation.id])
  useEffect(() => {
    void laden()
    fetchUnits().then(setEinheiten).catch(() => setEinheiten([]))
    fetchClipboardCount().then(setAblage).catch(() => setAblage(0))
  }, [laden])
  // N6-2: merken, sobald das Raster da ist — und nur, was es noch gibt (gelöschte Gruppen und Positionen fallen heraus).
  useEffect(() => {
    if (!raster) return
    const gruppen = new Set(raster.groups.map((g) => g.id))
    const positionen = new Set(raster.positions.map((p) => p.id))
    merkeKlappZustand(kalkulation.id, { zu: [...zu].filter((id) => gruppen.has(id)), texte: [...umgeschaltet].filter((id) => positionen.has(id)) })
  }, [kalkulation.id, raster, zu, umgeschaltet])

  const nach = useCallback(
    (neu: CalcRaster) => {
      setRaster(neu)
      onGeaendert()
    },
    [onGeaendert],
  )
  const tu = async (schritt: () => Promise<void>, ok?: string) => {
    setFehler(null)
    setHinweis(null)
    try {
      await schritt()
      if (ok) setHinweis(ok)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const gefiltert = Boolean(suche.trim() || zustand || gruppeFilter || artFilter)
  const zeilen = useMemo(() => {
    const q = suche.trim().toLowerCase()
    return (raster?.positions ?? []).filter(
      (p) =>
        (!q || `${p.displayNumber} ${p.shortText} ${p.longText ?? ''}`.toLowerCase().includes(q)) &&
        (!zustand || p.state === zustand) &&
        passtArt(p, artFilter) &&
        (!gruppeFilter || (gruppeFilter === '-' ? !p.groupId : p.groupId === gruppeFilter)),
    )
  }, [raster, suche, zustand, gruppeFilter, artFilter])

  // Eine LV-OZ („07.W1.01A") ist breiter als eine laufende Nummer — die Spalte wächst mit, statt sie abzuschneiden.
  const laengsteNr = useMemo(() => Math.max(3, ...(raster?.positions ?? []).map((p) => p.displayNumber.length)), [raster])
  const sichtbar = zeilen.filter((p) => !p.groupId || !zu.has(p.groupId))
  const reihe = useMemo(() => sichtbar.map((p) => p.id), [sichtbar])

  // Stufenweise klappen: erst die Gruppen Ebene für Ebene, als letzte Stufe die Texte der Positionen.
  const stufeAuf = useMemo(() => naechsteStufeAuf(raster?.groups ?? [], zu), [raster, zu])
  const stufeZu = useMemo(() => naechsteStufeZu(raster?.groups ?? [], zu), [raster, zu])
  const mitText = sichtbar.filter((p) => p.introText || p.longText || p.gapQuestion)
  const textAuf = mitText.some((p) => textOffen(p.id))
  const textFehlt = mitText.some((p) => !textOffen(p.id))
  const klappbar = Boolean(raster?.groups.length) || textAuf || textFehlt
  const spalten = useMemo(
    () =>
      SPALTEN_BASIS.filter((s) => kosten || (s.key !== 'mat' && s.key !== 'lohn')).map((s) =>
        s.key === 'nr'
          ? {
              ...s,
              // Mit den beiden Klapp-Pfeilen braucht der Kopf mehr Platz als eine kurze laufende Nummer.
              spur: `${Math.max(klappbar ? 6.75 : 4.5, laengsteNr * 0.55 + 1.75).toFixed(2)}rem`,
              min: Math.max(klappbar ? 104 : 56, laengsteNr * 9 + 24),
              // N1-32/N1-36: auf- und zuklappen über den Positionsnummern, wie die Pfeile darunter — je Klick eine Ebene.
              kopf: klappbar ? (
                <span className="flex items-center gap-1">
                  <button
                    type="button"
                    className="rounded hover:text-foreground disabled:opacity-40 disabled:hover:text-inherit"
                    title={stufeAuf ? 'Nächste Ebene aufklappen' : 'Texte der Positionen aufklappen'}
                    aria-label={stufeAuf ? 'Nächste Ebene aufklappen' : 'Texte der Positionen aufklappen'}
                    disabled={!stufeAuf && !textFehlt}
                    onClick={() => (stufeAuf ? setZu(stufeAuf) : zeigeTexte(true))}
                  >
                    <ChevronsUpDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="rounded hover:text-foreground disabled:opacity-40 disabled:hover:text-inherit"
                    title={textAuf ? 'Texte der Positionen zuklappen' : 'Unterste Ebene zuklappen'}
                    aria-label={textAuf ? 'Texte der Positionen zuklappen' : 'Unterste Ebene zuklappen'}
                    disabled={!textAuf && !stufeZu}
                    onClick={() => (textAuf ? zeigeTexte(false) : stufeZu && setZu(stufeZu))}
                  >
                    <ChevronsDownUp className="h-4 w-4" />
                  </button>
                  {s.label}
                </span>
              ) : undefined,
            }
          : s,
      ),
    [kosten, laengsteNr, klappbar, stufeAuf, stufeZu, textAuf, textFehlt, zeigeTexte],
  )

  const kopieren = () =>
    void tu(async () => {
      const r = await copyPositions(kalkulation.id, { ids: [...markiert] })
      setAblage((a) => a + r.copied)
    }, `${markiert.size} Position(en) in die Ablage kopiert — einfügen in einem Angebot, einem Projekt oder einer anderen Kalkulation.`)

  const einfuegen = () =>
    void tu(async () => {
      const r = await pastePositions(kalkulation.id, {})
      nach(r.data)
      setAblage(0)
      setHinweis(`${r.pasted} Position(en) eingefügt${r.skipped ? `, ${r.skipped} Zeilen ohne Position übersprungen` : ''}.`)
    })

  const { amRand } = useRandMarkieren({ behaelter, reihe, markiert, setMarkiert, onKopieren: kopieren })

  // Strg+V fügt ein — solange kein Feld den Fokus hat (Fokusregel wie Strg+C).
  useEffect(() => {
    if (!darf || mitLv) return
    const taste = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'v') return
      const fokus = document.activeElement
      if (fokus instanceof Element && fokus.closest('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      einfuegen()
    }
    document.addEventListener('keydown', taste)
    return () => document.removeEventListener('keydown', taste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darf, mitLv, kalkulation.id])

  const mitGezogen = (key: string) => (markiert.has(key) ? sichtbar.filter((p) => markiert.has(p.id)).map((p) => p.id) : [key])
  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      if (!raster) return
      const ids = raster.positions.map((p) => p.id)
      const bewegt = mitGezogen(key)
      const neu = verschiebeMehrereVor(ids, bewegt, vorKey)
      // Wer vor eine Position einer anderen Gruppe fällt, wandert in deren Gruppe.
      const zielGruppe = vorKey ? (raster.positions.find((p) => p.id === vorKey)?.groupId ?? null) : (raster.positions[raster.positions.length - 1]?.groupId ?? null)
      const gruppeAnders = bewegt.some((id) => raster.positions.find((p) => p.id === id)?.groupId !== zielGruppe)
      if (neu === ids && !gruppeAnders) return
      void tu(async () => nach(await reorderPositions(kalkulation.id, { ids: [...neu], moved: bewegt, groupId: zielGruppe })))
    },
  })

  /** Enter springt in dieselbe Spalte der nächsten Zeile (AP-32 Nachtrag 11). */
  const enterWeiter = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey) return
    const feld = e.target as HTMLElement
    const spalte = feld.getAttribute('data-spalte')
    if (!spalte || feld.tagName === 'TEXTAREA') return
    const alle = [...(behaelter.current?.querySelectorAll<HTMLElement>(`[data-spalte="${spalte}"]`) ?? [])]
    const i = alle.indexOf(feld)
    const naechstes = alle.slice(i + 1).find((x) => !(x as HTMLInputElement).disabled)
    if (naechstes) {
      e.preventDefault()
      naechstes.focus()
      ;(naechstes as HTMLInputElement).select?.()
    }
  }

  if (!raster) return <p className="text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</p>

  const gruppenVon = (parentId: string | null) => raster.groups.filter((g) => g.parentId === parentId).sort((a, b) => a.sortOrder - b.sortOrder)
  const leer = raster.positions.length === 0 && raster.groups.length === 0
  // N1-19: Die leere Kalkulation zeigt nur die Wahl des Einstiegs; „Erste Position“ öffnet das Raster.
  const auswahl = leer && darf && !beginnen
  const summe = raster.positions.filter((p) => !['OPTIONAL', 'ALTERNATIVE', 'TEXT'].includes(p.kind)).reduce((s, p) => s + p.totalPrice, 0)
  const stunden = raster.positions.filter((p) => !['OPTIONAL', 'ALTERNATIVE', 'TEXT'].includes(p.kind)).reduce((s, p) => s + p.hours, 0)
  // Wie im Rechenkern (H-9): Text zählt nicht, eine offene Lücke ist weder ausgepreist noch offen.
  const mitPreis = raster.positions.filter((p) => p.kind !== 'TEXT')
  const offen = mitPreis.filter((p) => p.state === 'OPEN').length
  const luecken = mitPreis.filter((p) => p.state === 'GAP_OPEN').length
  const gezogeneIds = new Set(gezogen ? mitGezogen(gezogen) : [])
  const gezogenePos = gezogen ? raster.positions.find((p) => p.id === gezogen) : undefined

  const zeile = (p: CalcPosition) => (
    <PositionsZeile
      key={p.id}
      kalkulationId={kalkulation.id}
      p={p}
      einheiten={einheiten}
      darf={darf}
      kosten={kosten}
      markiert={markiert.has(p.id)}
      gezogen={gezogeneIds.has(p.id)}
      ziellinie={ziel?.key === p.id ? ziel.kante : null}
      onRand={amRand(p.id)}
      onGeaendert={() => {
        void laden()
        onGeaendert()
      }}
      onOeffnen={() => onOeffnen(p.id)}
      onLoeschen={() =>
        void tu(async () => {
          nach(await deletePositions(kalkulation.id, [p.id]))
          setGeloescht((g) => [...g, { trashId: p.id, name: p.shortText }])
        })
      }
      texte={textOffen(p.id)}
      onTexte={() => setUmgeschaltet((u) => (u.has(p.id) ? new Set([...u].filter((x) => x !== p.id)) : new Set([...u, p.id])))}
    />
  )

  const GruppenKopf = ({ g }: { g: CalcGroup }) => (
    <div className="flex items-center gap-2 bg-muted/60 px-3 py-1.5 text-sm font-semibold" style={{ paddingLeft: `${0.75 + g.level * 1.25}rem` }}>
      <button
        type="button"
        className="text-muted-foreground"
        aria-label={zu.has(g.id) ? 'aufklappen' : 'zuklappen'}
        onClick={() => setZu((z) => (z.has(g.id) ? new Set([...z].filter((x) => x !== g.id)) : new Set([...z, g.id])))}
      >
        {zu.has(g.id) ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      <span className="tabular-nums text-muted-foreground">{g.displayNumber}</span>
      <span className="flex-1 truncate">{g.name}</span>
      <span className="tabular-nums">{geldText(g.subtotal)}</span>
      {darf && (
        <span className="flex items-center gap-1.5" data-auswahl-behalten>
          <button type="button" className="text-xs font-normal text-primary hover:underline" onClick={() => void tu(() => copyPositionsGruppe(g.id), 'Gruppe in die Ablage kopiert.')}>
            kopieren
          </button>
          {!g.oz && (
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive"
              title="Gruppe auflösen — die Positionen bleiben"
              onClick={async () => {
                if (!(await bestaetige(`Gruppe „${g.name}" auflösen? Die Positionen bleiben und rücken eine Ebene hinauf.`, { ok: 'Auflösen' }))) return
                void tu(async () => nach(await deleteGroup(kalkulation.id, g.id)))
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </span>
      )}
    </div>
  )

  const copyPositionsGruppe = async (gid: string) => {
    const r = await copyPositions(kalkulation.id, { groupIds: [gid] })
    setAblage((a) => a + r.copied)
  }

  // Mit Filter nur Gruppen, unter denen (auch tiefer) etwas passt — sonst stünden lauter leere Köpfe da.
  const hatTreffer = (g: CalcGroup): boolean => zeilen.some((p) => p.groupId === g.id) || gruppenVon(g.id).some(hatTreffer)
  const baum = (parentId: string | null): React.ReactNode[] =>
    gruppenVon(parentId).filter((g) => !gefiltert || hatTreffer(g)).flatMap((g) => [
      <GruppenKopf key={`g-${g.id}`} g={g} />,
      ...(zu.has(g.id) ? [] : [...zeilen.filter((p) => p.groupId === g.id).map(zeile), ...baum(g.id)]),
    ])

  return (
    <div className="space-y-3">
      {/* Werkzeugleiste */}
      {!auswahl && (
      <div className="flex flex-wrap items-center gap-2" data-auswahl-behalten>
        <input className={`${inlineInput} !w-56`} placeholder="Suche in Nr., Kurztext, Langtext" value={suche} onChange={(e) => setSuche(e.target.value)} />
        <select className={`${inlineInput} !w-40`} value={zustand} onChange={(e) => setZustand(e.target.value)} aria-label="Zustand">
          <option value="">alle Zustände</option>
          {Object.entries(ZUSTAND_ANZEIGE).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <select className={`${inlineInput} !w-52`} value={artFilter} onChange={(e) => setArtFilter(e.target.value as PositionsArtFilter)} aria-label="Art der Positionen">
          {POSITIONS_ART_FILTER.map((f) => (
            <option key={f.wert} value={f.wert}>
              {f.text}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground" title="Einleitungs- und Langtexte unter den Positionen, wie im gedruckten LV">
          <input type="checkbox" checked={texte} onChange={(e) => zeigeTexte(e.target.checked)} /> Texte einblenden
        </label>
        {raster.groups.length > 0 && (
          <select className={`${inlineInput} !w-44`} value={gruppeFilter} onChange={(e) => setGruppeFilter(e.target.value)} aria-label="Gruppe">
            <option value="">alle Gruppen</option>
            <option value="-">ohne Gruppe</option>
            {raster.groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.displayNumber} {g.name}
              </option>
            ))}
          </select>
        )}
        <span className="flex-1" />
        {markiert.size > 0 && (
          <>
            <span className="text-sm text-muted-foreground">{markiert.size} markiert</span>
            {markiert.size === 1 &&
              (() => {
                const muster = raster.positions.find((p) => markiert.has(p.id))
                const gleiche = muster ? raster.positions.filter((p) => p.kind !== 'TEXT' && p.shortText.trim() === muster.shortText.trim() && (p.unitCode ?? p.unitRaw ?? '') === (muster.unitCode ?? muster.unitRaw ?? '')) : []
                return gleiche.length > 1 ? (
                  <button
                    type="button"
                    className="rounded-md border border-border px-2.5 py-1 text-sm"
                    title="Positionen mit gleichem Kurztext und gleicher Einheit markieren — etwa, um sie gemeinsam mit dem Blatt dieser Position zu belegen"
                    onClick={() => setMarkiert(new Set(gleiche.map((p) => p.id)))}
                  >
                    {gleiche.length} gleiche markieren
                  </button>
                ) : null
              })()}
            <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm" onClick={kopieren}>
              <Copy className="h-4 w-4" /> Kopieren
            </button>
            {darf && kosten && (
              <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm" onClick={() => setAnwendenOffen(true)}>
                <Layers className="h-4 w-4" /> Blatt anwenden …
              </button>
            )}
            {darf && (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm text-destructive"
                onClick={() =>
                  void tu(async () => {
                    const namen = raster.positions.filter((p) => markiert.has(p.id))
                    nach(await deletePositions(kalkulation.id, [...markiert]))
                    setGeloescht((g) => [...g, ...namen.map((p) => ({ trashId: p.id, name: p.shortText }))])
                    setMarkiert(new Set())
                  })
                }
              >
                <Trash2 className="h-4 w-4" /> Löschen
              </button>
            )}
          </>
        )}
        {darf && mitLv && <span className="text-xs text-muted-foreground" title="Das LV gibt Positionen und Gliederung vor — ausgepreist wird nur.">LV: keine neuen Positionen</span>}
        {darf && !mitLv && (
          <>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm"
              onClick={async () => {
                const name = await frage('Name der neuen Gruppe', '', { pflicht: true, ok: 'Anlegen' })
                if (name) void tu(async () => (await createGroup(kalkulation.id, { name }), await laden()))
              }}
            >
              <FolderPlus className="h-4 w-4" /> Gruppe
            </button>
            <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm disabled:opacity-40" disabled={!ablage} onClick={einfuegen} title="Strg+V">
              <ClipboardPaste className="h-4 w-4" /> Einfügen{ablage ? ` (${ablage})` : ''}
            </button>
          </>
        )}
        <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm" onClick={() => setKorbOffen(true)}>
          <Trash2 className="h-4 w-4" /> Papierkorb{raster.trashCount ? ` (${raster.trashCount})` : ''}
        </button>
      </div>
      )}

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {hinweis && <p className="text-sm text-muted-foreground">{hinweis}</p>}
      {darf && geloescht.length > 0 && (
        <RueckgaengigHinweis
          geloescht={geloescht}
          onRueckgaengig={(id) =>
            void tu(async () => {
              nach(await restorePositions(kalkulation.id, [id]))
              setGeloescht((g) => g.filter((x) => x.trashId !== id))
            })
          }
          onSchliessen={() => setGeloescht([])}
        />
      )}

      {!auswahl && (
      <div ref={behaelter} onKeyDown={enterWeiter}>
        <ZiehBereich
          dndProps={dndProps}
          vorschau={
            gezogenePos && (
              <>
                {gezogeneIds.size > 1 && <span className="mr-1 rounded bg-primary px-1.5 text-xs text-primary-foreground">{gezogeneIds.size} Positionen</span>}
                <span className="font-medium">{gezogenePos.shortText}</span>
              </>
            )
          }
        >
          <ZeilenListe
            spalten={spalten}
            fuss={
              <div className="border-t-2 border-border bg-muted/40 px-3 py-1.5 text-sm">
                <div className="flex flex-wrap justify-between gap-2 font-medium">
                  <span>
                    {mitPreis.length} Positionen · {mitPreis.length - offen - luecken} ausgepreist · {offen} offen{luecken ? ` · ${luecken} Lücke(n) offen` : ''}
                    {gefiltert && <span className="ml-2 text-xs font-normal text-muted-foreground">(Filter aktiv — die Summe gilt für alle)</span>}
                  </span>
                  <span className="tabular-nums">
                    {stundenText(stunden)} h · Angebotssumme {geldText(summe)}
                  </span>
                </div>
                {raster.sums && <Summenzeile summen={raster.sums} kosten={kosten} />}
              </div>
            }
          >
            {zeilen.filter((p) => !p.groupId).map(zeile)}
            {baum(null)}
            {darf && !gefiltert && !mitLv && (
              <PositionsZeile
                key={`neu-${neuSchluessel}`}
                kalkulationId={kalkulation.id}
                einheiten={einheiten}
                darf={darf}
                kosten={kosten}
                autoFokus={neuFokus || leer}
                onGeaendert={() => void laden()}
                onAnlegen={async (dto, perTastatur) => {
                  const neu = await createPosition(kalkulation.id, dto)
                  await laden()
                  onGeaendert()
                  setNeuFokus(perTastatur)
                  setNeuSchluessel((k) => k + 1)
                  // Die erste Position öffnet ihr Blatt (4.9).
                  if (leer) onOeffnen(neu.id)
                }}
              />
            )}
            {(gefiltert || !darf) && zeilen.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">{gefiltert ? 'Keine Position passt zum Filter.' : 'Noch keine Positionen.'}</p>}
          </ZeilenListe>
        </ZiehBereich>
      </div>
      )}

      {auswahl && (
        <section className="rounded-xl border border-primary/30 bg-primary/5 p-5">
          <h3 className="text-base font-semibold">Wie willst du beginnen?</h3>
          <p className="mb-4 text-sm text-muted-foreground">Die Kalkulation hat noch keine Position.</p>
          <div className="grid gap-4 md:grid-cols-3">
            <Einstieg
              icon={ListPlus}
              titel="Erste Position"
              text="Menge, Einheit und Bezeichnung eingeben — Enter legt sie an und öffnet ihr Blatt."
              knopf="Erste Position anlegen"
              haupt
              onClick={() => {
                setBeginnen(true)
                // Das Raster misst erst seine Breite und baut die Felder dann neu — die neue Zeile
                // entsteht danach noch einmal, damit ihr erstes Feld den Fokus behält.
                requestAnimationFrame(() => requestAnimationFrame(() => setNeuSchluessel((k) => k + 1)))
              }}
            />
            {lvEinlesen && (
              <Einstieg
                icon={FileInput}
                titel="LV einlesen"
                marke="Baustein LV"
                text="ONLV, ÖNORM B 2063 oder GAEB — legt Gruppen und Positionen an; geht, solange noch keine Position besteht."
                knopf="LV einlesen …"
                onClick={lvEinlesen}
              />
            )}
            <Einstieg
              icon={ClipboardPaste}
              titel="Einfügen"
              text={ablage ? `Aus der Ablage: ${ablage}.` : 'Aus der Ablage — erst in einer Vorlage, einer anderen Kalkulation oder einem Beleg kopieren.'}
              knopf="Einfügen (Strg+V)"
              aus={!ablage}
              onClick={einfuegen}
            />
          </div>
          {(raster.trashCount ?? 0) > 0 && (
            <button type="button" className="mt-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" onClick={() => setKorbOffen(true)}>
              <Trash2 className="h-4 w-4" /> Papierkorb ({raster.trashCount})
            </button>
          )}
        </section>
      )}

      {anwendenOffen && (
        <BlattAnwendenDialog
          kalkulationId={kalkulation.id}
          positionen={raster.positions}
          markiert={markiert}
          onClose={() => setAnwendenOffen(false)}
          onDone={(n) => {
            setAnwendenOffen(false)
            setHinweis(`Blatt auf ${n} Position(en) angewendet.`)
            void laden()
            onGeaendert()
          }}
        />
      )}
      {korbOffen && (
        <PapierkorbFenster
          titel="Papierkorb der Positionen"
          aufbewahrung="30 Tage"
          laden={async () => (await fetchPositionTrash(kalkulation.id)).map((p) => ({ id: p.id, name: `${p.shortText} · ${p.quantity.toLocaleString('de-DE')} ${p.unitCode ?? ''}`, deletedAt: p.deletedAt, deletedByName: null }))}
          onWiederherstellen={(id) => void tu(async () => nach(await restorePositions(kalkulation.id, [id])))}
          onSchliessen={() => setKorbOffen(false)}
        />
      )}
    </div>
  )
}

/** N1-19: eine Karte der Einstiegswahl — ganze Fläche klickbar, der Hauptweg in der Firmenfarbe. */
function Einstieg({
  icon: Icon,
  titel,
  text,
  knopf,
  marke,
  haupt,
  aus,
  onClick,
}: {
  icon: typeof ListPlus
  titel: string
  text: string
  knopf: string
  marke?: string
  haupt?: boolean
  aus?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={aus}
      onClick={onClick}
      className="group flex flex-col items-start gap-2 rounded-lg border-2 border-border bg-card p-4 text-left transition-colors hover:border-primary hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:shadow-none"
    >
      <span className="flex w-full items-center gap-2">
        <span className="rounded-md bg-primary/10 p-2 text-primary">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <span className="text-base font-semibold">{titel}</span>
        {marke && <span className="ml-auto rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-900 dark:bg-violet-500/20 dark:text-violet-200">{marke}</span>}
      </span>
      <span className="flex-1 text-sm text-muted-foreground">{text}</span>
      <span className={`mt-1 rounded-md px-3 py-1.5 text-sm font-medium ${haupt ? 'bg-primary text-primary-foreground' : 'border border-border group-hover:border-primary group-hover:text-primary'}`}>{knopf}</span>
    </button>
  )
}
